import React from 'react';

import FormControl from '../FormControl';
import FormHelperText from '../FormHelperText';
import Input from '../Input';
import InputLabel from '../InputLabel';
import { InputBaseComponentProps } from '../InputBase';
import MUIMenuItem from '../MenuItem';
import MUISelect, { SelectProps } from '../Select';

export interface SelectOptionProps {
  value: string | number;
  title?: string | number | React.ReactNode;
  disabled?: boolean;
  dataTestLocator?: string;
}

interface Props {
  error?: string;
  required?: boolean;
  helperText?: string;
  label?: string;
  value?: string | number;
  name: string;
  options: SelectOptionProps[];
  onChange?: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  fullWidth?: boolean;
  className?: string;
  disabled?: boolean;
  readOnly?: boolean;
  richContent?: boolean;
  inputProps?: InputBaseComponentProps;
  classes?: SelectProps['classes'];
}

const Select: React.FC<Props> = ({
  error,
  value,
  label,
  name,
  helperText,
  options,
  required,
  fullWidth,
  disabled,
  className,
  readOnly,
  onChange = () => undefined,
  richContent = false,
  inputProps,
  classes,
  ...rest
}) => {
  const renderedOptions = options.map(option => {
    if (richContent) {
      return (
        <MUIMenuItem
          key={option.value}
          value={option.value}
          data-test-locator={option.dataTestLocator || null}
        >
          {option.title || option.value}
        </MUIMenuItem>
      );
    }

    return (
      <option
        key={option.value}
        value={option.value}
        data-test-locator={option.dataTestLocator || null}
      >
        {option.title || option.value}
      </option>
    );
  });

  return (
    <FormControl
      className={className}
      fullWidth={fullWidth}
      required={required}
      error={Boolean(error)}
      {...rest}
    >
      {label && (
        <InputLabel
          shrink={
            // because we have many places setting the first option for our select
            // we need to somehow prevent the user of this input to decide if he should or not
            // shrink the label
            // When you already selected a value just shrink the value
            // if not, check if the given options include option with value empty and title
            // it means the user is given the component a default option which should be rendered
            // and not be overlapped by the label
            // Be careful with a value that equals to 0 (zero), as that will make the next assertion false.
            value
              ? true
              : options.some(
                  option => option.value === '' && option.title !== '',
                )
          }
          htmlFor={name}
        >
          {label}
        </InputLabel>
      )}
      <MUISelect
        native={!richContent && !readOnly}
        value={value}
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        onChange={onChange}
        name={name}
        input={<Input id={name} readOnly={readOnly} />}
        disabled={disabled}
        inputProps={inputProps || undefined}
        data-test-locator="Select Component"
        classes={classes}
      >
        {renderedOptions}
      </MUISelect>
      {helperText && <FormHelperText>{helperText}</FormHelperText>}
      {error && <FormHelperText>{error}</FormHelperText>}
    </FormControl>
  );
};

export default Select;
